// src/config/mobileRoutes.js
/**
 * Configuración centralizada de rutas móviles
 */

export const MOBILE_ROUTE_CONFIG = {
  // Tipos de token y sus rutas de destino
  TOKEN_DESTINATIONS: {
    // Flujo de autogestión del cliente
    'customer_self_service': {
      basePath: '/m/onboarding',
      targetPath: '/flow',
      requiresRedirect: true,
      supportsFromStep: true,
      description: 'Cliente autogestiona su proceso de onboarding completo',
      expectedScopeFields: ['customer_id', 'request_id'],
      expectedScopeActions: [], // Los scopes son dinámicos basados en los pasos del flujo
      isDynamic: true, // Indica que los scopes son dinámicos
      allowedRoutes: ['/m/onboarding/:token/flow'] // Solo puede acceder al flujo
    },
    
    // Carga de documentos durante flujo de solicitud.
    'onboarding': {
      basePath: '/m/onboarding',
      targetPath: '', 
      requiresRedirect: false,
      description: 'Carga de documentos durante proceso de solicitud',
      expectedScopeFields: ['customer_id', 'request_id'],
      expectedScopeActions: ['document_upload'],
      allowedRoutes: ['/m/onboarding/:token'] // Solo puede acceder a carga de documentos
    },
    
    // Acceso de sucursal a múltiples solicitudes.
    'branch': {
      basePath: '/m/docs',
      targetPath: '/requests',
      requiresRedirect: true,
      description: 'Vendedor de sucursal gestiona documentos de múltiples solicitudes',
      expectedScopeFields: [], // branch_id se infiere del usuario autenticado
      expectedScopeActions: ['document_upload', 'document_view', 'requests_management'],
      allowedRoutes: ['/m/docs/:token', '/m/docs/:token/requests'] // Puede acceder a ambas rutas de docs
    },
    
    // Acceso a documentos de UNA solicitud específica.
    'request': {
      basePath: '/m/docs',
      targetPath: '/requests',
      requiresRedirect: true,
      description: 'Acceso a documentos de una solicitud específica',
      expectedScopeFields: ['request_id', 'customer_id'],
      expectedScopeActions: ['document_upload', 'document_view'],
      allowedRoutes: ['/m/docs/:token', '/m/docs/:token/requests'] // Puede acceder a ambas rutas de docs
    },
    
    // Cliente accede a documentos de SU solicitud.
    'customer': {
      basePath: '/m/docs', 
      targetPath: '/requests',
      requiresRedirect: true,
      description: 'Cliente accede a documentos de su propia solicitud',
      expectedScopeFields: ['request_id', 'customer_id'],
      expectedScopeActions: ['document_upload', 'document_view'],
      allowedRoutes: ['/m/docs/:token', '/m/docs/:token/requests'] // Puede acceder a ambas rutas de docs
    },

    // Cliente vincula una tarjeta desde una solicitud puntual.
    'card_linking': {
      basePath: '/m/docs',
      targetPath: '',
      requiresRedirect: false,
      description: 'Cliente vincula una tarjeta desde su celular',
      expectedScopeFields: ['customer_id', 'request_id'],
      expectedScopeActions: ['card_linking'],
      allowedRoutes: ['/m/docs/:token']
    },

    // Firma mobile de contrato commerce para una solicitud puntual.
    'commerce_mobile_signature': {
      basePath: '/m/signature',
      targetPath: '',
      requiresRedirect: false,
      description: 'Solicitante firma el contrato desde su celular',
      expectedScopeFields: ['request_uid', 'contract_id', 'signer_type', 'flow'],
      expectedScopeValues: {
        signer_type: 'applicant',
        flow: 'commerce_mobile_signature'
      },
      expectedScopeActions: ['contract_signing'],
      allowedRoutes: ['/m/signature/:token']
    }
  },

  // Rutas de error
  ERROR_ROUTES: {
    invalid_token: '/m/error/invalid_token',
    token_expired: '/m/error/token_expired',
    unauthorized: '/m/error/unauthorized'
  },

  // Rutas que requieren customer_id y request_id
  CUSTOMER_FLOW_PATHS: ['/flow'],

  // Rutas que actúan como entry points (necesitan redirección)
  ENTRY_POINT_PATHS: [
    '/m/docs/:token',
    '/m/onboarding/:token',
    '/m/flow/:token'
  ]
};

/**
 * Determina la ruta de destino basada en el scope del token
 * @param {string} token - Token de la URL
 * @param {Object} scope - Scope del token
 * @param {string} tokenType - Tipo de token
 * @param {string} fromStep - Paso de origen (opcional)
 * @returns {string|null} Ruta de destino o null si no requiere redirección
 */
export const determineDestinationRoute = (token, scope, tokenType, fromStep = null) => {
  const config = MOBILE_ROUTE_CONFIG.TOKEN_DESTINATIONS[tokenType];
  
  if (!config) {
    return MOBILE_ROUTE_CONFIG.ERROR_ROUTES.invalid_token;
  }

  // Construir la ruta de destino
  let destinationRoute = `${config.basePath}/${token}${config.targetPath}`;

  // Agregar query parameter si es necesario y soportado
  if (fromStep && config.supportsFromStep) {
    destinationRoute += `?from_step=${fromStep}`;
  }

  return destinationRoute;
};

/**
 * Verifica si la ruta actual necesita redirección
 * @param {string} currentPath - Ruta actual
 * @param {string} token - Token de la URL
 * @param {string} tokenType - Tipo de token
 * @returns {boolean} True si necesita redirección
 */
export const shouldRedirect = (currentPath, token, tokenType) => {
  const config = MOBILE_ROUTE_CONFIG.TOKEN_DESTINATIONS[tokenType];
  
  if (!config || !config.requiresRedirect) {
    return false;
  }

  // Verificar si estamos en la ruta de entry point
  return MOBILE_ROUTE_CONFIG.ENTRY_POINT_PATHS.some(entryPath => {
    const pathPattern = entryPath.replace(':token', token);
    return currentPath === pathPattern;
  });
};

/**
 * Verifica si una ruta requiere customer_id y request_id
 * @param {string} pathname - Pathname a verificar
 * @returns {boolean} True si requiere customer flow data
 */
export const requiresCustomerFlowData = (pathname) => {
  return MOBILE_ROUTE_CONFIG.CUSTOMER_FLOW_PATHS.some(path => 
    pathname.includes(path)
  );
};

/**
 * Comprueba que el scope contenga los campos y acciones esperadas para el tipo de token
 * @param {string} tokenType - Tipo de token
 * @param {Object} scope - Scope del token (puede incluir scope.scope para acciones)
 * @returns {boolean} True si el scope es válido
 */
export const validateTokenScope = (tokenType, scope) => {
  const config = MOBILE_ROUTE_CONFIG.TOKEN_DESTINATIONS[tokenType];
  
  if (!config) {
    return false; // Tipo de token no reconocido
  }
  
  // 1. Comprobar campos requeridos (IDs)
  if (config.expectedScopeFields) {
    const fieldsValid = config.expectedScopeFields.every(field => 
      (scope[field] !== null && scope[field] !== undefined) ||
      (field === 'request_id' && scope.request_id !== null && scope.request_id !== undefined)
    );
    
    if (!fieldsValid) {
      console.warn(`Token tipo '${tokenType}' falta campos requeridos:`, config.expectedScopeFields);
      return false;
    }
  }

  if (config.expectedScopeValues) {
    const valuesValid = Object.entries(config.expectedScopeValues).every(([field, expectedValue]) =>
      scope[field] === expectedValue
    );

    if (!valuesValid) {
      console.warn(`Token tipo '${tokenType}' tiene valores de scope inesperados:`, config.expectedScopeValues);
      return false;
    }
  }
  
  // 2. Para tokens dinámicos (customer_self_service), no validar acciones específicas
  if (config.isDynamic) {
    return true; // Solo validamos campos, las acciones son dinámicas
  }
  
  // 3. Comprobar acciones del scope (solo para tokens estáticos)
  if (config.expectedScopeActions && config.expectedScopeActions.length > 0) {
    const scopeActions = Array.isArray(scope.permissions)
      ? scope.permissions
      : (Array.isArray(scope.scope) ? scope.scope : (scope.scope ? [scope.scope] : []));
    
    // Verificar que al menos una acción esperada esté presente
    const hasRequiredAction = config.expectedScopeActions.some(expectedAction =>
      scopeActions.includes(expectedAction)
    );
    
    if (!hasRequiredAction) {
      console.warn(`Token tipo '${tokenType}' no tiene acciones requeridas. Esperadas:`, config.expectedScopeActions, 'Recibidas:', scopeActions);
      return false;
    }
  }
  
  return true;
};
